import { Button } from '@/components/ui/button'
import * as Card from '@/components/ui/card'
import * as Field from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { api } from '@api'
import AnimatedBackground from '@components/AnimatedBackground'
import { createForm, valiForm } from '@modular-forms/solid'
import { useNavigate } from '@solidjs/router'
import { createSignal, onMount } from 'solid-js'
import { css } from 'styled-system/css'
import { Grid } from 'styled-system/jsx'
import { text } from 'styled-system/recipes'
import * as v from 'valibot'

export const UpdatePasswordSchema = v.object({
  password: v.pipe(
    v.string(),
    v.minLength(6, 'Password must be at least 6 characters'),
  ),
  confirmPassword: v.pipe(
    v.string(),
    v.minLength(1, 'Please confirm your password'),
  ),
})

export type UpdatePasswordValues = v.InferInput<typeof UpdatePasswordSchema>

export default function UpdatePassword() {
  const navigate = useNavigate()
  const [loading, setLoading] = createSignal(false)
  const [errorMsg, setErrorMsg] = createSignal('')

  const [form, { Form, Field: FormField }] = createForm<UpdatePasswordValues>({
    validate: valiForm(UpdatePasswordSchema),
    revalidateOn: 'change',
    initialValues: {
      password: '',
      confirmPassword: '',
    },
  })

  onMount(async () => {
    const {
      data: { session },
    } = await api().auth.getSession()
    if (!session) {
      navigate('/auth/login')
    }
  })

  const handleUpdate = async (values: UpdatePasswordValues) => {
    setErrorMsg('')

    if (values.password !== values.confirmPassword) {
      setErrorMsg('Passwords do not match.')
      return
    }

    setLoading(true)

    const { error } = await api().auth.updateUser({
      password: values.password,
    })

    if (error) {
      setErrorMsg(error.message)
      setLoading(false)
    } else {
      navigate('/')
    }
  }

  return (
    <main
      class={css({
        display: 'grid',
        alignItems: { base: 'center', mdDown: 'start' },
        pt: { mdDown: 8 },
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        bg: 'white',
      })}
    >
      <AnimatedBackground />
      <Card.Root minW={450}>
        <Card.Header display="flex" justifyContent="center" alignItems="center">
          <img
            class={css({ height: '40px', width: '40px' })}
            src={'/logo.png'}
            alt="sniper circle logo"
          />
          <Heading textStyle={{ base: 'lg' }} lineHeight="35px">
            Set New Password
          </Heading>
        </Card.Header>

        <Card.Body>
          <Form onSubmit={handleUpdate}>
            <Grid gap={4}>
              <Text textStyle="sm" class={css({ mb: 2, textAlign: 'center' })}>
                Please enter your new password below.
              </Text>

              <FormField name="password">
                {(field, formFieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Field.Label>New Password</Field.Label>
                    <Input
                      {...formFieldProps}
                      value={field.value ?? ''}
                      type="password"
                      placeholder="New Password"
                      disabled={loading()}
                    />
                    <Field.ErrorText>
                      {field.error && <div>{field.error}</div>}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              </FormField>

              <FormField name="confirmPassword">
                {(field, formFieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Field.Label>Confirm New Password</Field.Label>
                    <Input
                      {...formFieldProps}
                      value={field.value ?? ''}
                      type="password"
                      placeholder="Confirm New Password"
                      disabled={loading()}
                    />
                    <Field.ErrorText>
                      {field.error && <div>{field.error}</div>}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              </FormField>

              {errorMsg() && (
                <p
                  class={`${text({ variant: 'danger' })} ${css({ color: 'red', fontSize: 'sm' })}`}
                >
                  {errorMsg()}
                </p>
              )}

              <Button
                type="submit"
                colorPalette="jade"
                loading={loading()}
                disabled={loading()}
              >
                {loading() ? 'Updating...' : 'Update Password'}
              </Button>
            </Grid>
          </Form>
        </Card.Body>
      </Card.Root>
    </main>
  )
}
